import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { BracketMatch } from "../components/BracketView";
import { BracketView } from "../components/BracketView";
import { SectionCard } from "../components/SectionCard";
import { TournamentStatusPill } from "../components/TournamentStatusPill";
import { useBreadcrumbs } from "../context/BreadcrumbContext";
import { api } from "../lib/api";
import type { AdminTournamentDetail, TournamentStatus } from "../types/domain";

const NEXT_STATUSES: Record<
  string,
  { target: TournamentStatus; label: string }[]
> = {
  draft: [{ target: "registration_open", label: "Ouvrir les inscriptions" }],
  registration_open: [
    { target: "registration_closed", label: "Fermer les inscriptions" },
  ],
  registration_closed: [
    { target: "registration_open", label: "Rouvrir les inscriptions" },
    { target: "in_progress", label: "Démarrer le tournoi" },
  ],
  in_progress: [{ target: "finished", label: "Terminer le tournoi" }],
  finished: [],
  cancelled: [],
};

export function AdminTournamentPage() {
  const { tournamentId = "" } = useParams();
  const { setTrail } = useBreadcrumbs();
  const [payload, setPayload] = useState<AdminTournamentDetail | null>(null);
  const [bracketMatches, setBracketMatches] = useState<BracketMatch[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    setTrail([]);
    void api.getAdminTournament(tournamentId).then((data) => {
      if (!active) return;
      setPayload(data);
      setTrail([
        { label: "Tournois", to: "/tournaments" },
        { label: data.tournament.name, to: `/tournaments/${tournamentId}` },
        { label: "Administration" },
      ]);
    });
    void api.listFinals(tournamentId).then((matches) => {
      if (!active) return;
      setBracketMatches(matches);
    });
    return () => {
      active = false;
    };
  }, [tournamentId, setTrail]);

  if (!payload) {
    return (
      <div className="space-y-6 animate-pulse">
        <div>
          <div className="flex items-center gap-3">
            <div className="h-7 w-48 rounded bg-gray-200" />
            <div className="h-6 w-20 rounded-full bg-gray-200" />
          </div>
          <div className="mt-3 flex gap-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-5 w-20 rounded-full bg-gray-100" />
            ))}
          </div>
        </div>
        {[1, 2].map((i) => (
          <div key={i} className="card">
            <div className="h-4 w-32 rounded bg-gray-200" />
            <div className="mt-3 h-10 w-40 rounded bg-gray-100" />
          </div>
        ))}
      </div>
    );
  }

  const { tournament, metrics, pendingGames } = payload;

  const transitions = NEXT_STATUSES[tournament.status] ?? [];
  const isTerminal =
    tournament.status === "finished" || tournament.status === "cancelled";

  async function handleTransition(target: TournamentStatus) {
    setBusy(true);
    try {
      const result = await api.transitionTournamentStatus(tournamentId, target);
      setPayload(result);
    } catch {
      // error already logged
    } finally {
      setBusy(false);
    }
  }

  async function handleRecomputeScores() {
    setBusy(true);
    try {
      const result = await api.recomputeTournamentScores(tournamentId);
      setPayload(result);
    } catch {
      // error already toasted
    } finally {
      setBusy(false);
    }
  }

  async function handleGenerateFinals() {
    setBusy(true);
    try {
      const matches = await api.generateFinals(tournamentId);
      setBracketMatches(matches);
      const result = await api.getAdminTournament(tournamentId);
      setPayload(result);
    } catch {
      // error already logged
    } finally {
      setBusy(false);
    }
  }

  async function handleCreateFinalsGame(matchId: string) {
    setBusy(true);
    try {
      const matches = await api.createFinalsGame(tournamentId, matchId);
      setBracketMatches(matches);
    } catch {
      // error already toasted
    } finally {
      setBusy(false);
    }
  }

  async function handleForceValidate(gameId: string) {
    setBusy(true);
    try {
      const result = await api.forceValidateGame(tournamentId, gameId);
      setPayload(result);
    } catch {
      // error already toasted
    } finally {
      setBusy(false);
    }
  }

  async function handleCancelGame(gameId: string) {
    setBusy(true);
    try {
      const result = await api.cancelTournamentGame(tournamentId, gameId);
      setPayload(result);
    } catch {
      // error already toasted
    } finally {
      setBusy(false);
    }
  }

  async function handleDissolveTeam(teamId: string) {
    setBusy(true);
    try {
      const result = await api.dissolveTeam(tournamentId, teamId);
      setPayload(result);
    } catch {
      // error already logged
    } finally {
      setBusy(false);
    }
  }

  async function handleReactivateTeam(teamId: string) {
    setBusy(true);
    try {
      const result = await api.reactivateTeam(tournamentId, teamId);
      setPayload(result);
    } catch {
      // error already toasted
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-ink">{tournament.name}</h1>
            <TournamentStatusPill status={tournament.status} />
          </div>
          {!isTerminal && (
            <Link
              to={`/admin/tournaments/${tournamentId}/edit`}
              className="shrink-0 text-sm text-smoke hover:text-ink"
            >
              Modifier
            </Link>
          )}
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {metrics.map((metric) => (
            <span
              key={metric.label}
              className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-smoke"
            >
              {metric.value} {metric.label.toLowerCase()}
            </span>
          ))}
        </div>

        <div className="mt-4 flex flex-wrap gap-3">
          {transitions.map(({ target, label }) => (
            <button
              key={target}
              type="button"
              className="btn-primary"
              disabled={busy}
              onClick={() => handleTransition(target)}
            >
              {label}
            </button>
          ))}
          {!isTerminal && (
            <button
              type="button"
              className="btn-secondary text-red-600"
              disabled={busy}
              onClick={() => handleTransition("cancelled")}
            >
              Annuler le tournoi
            </button>
          )}
          <button
            type="button"
            className="btn-secondary"
            disabled={busy}
            onClick={handleRecomputeScores}
          >
            Recalculer les scores
          </button>
        </div>
      </div>

      {/* Bracket (when finals exist, show on top) */}
      {bracketMatches.length > 0 && (
        <SectionCard
          title="Phase finale"
          action={
            (tournament.status === "in_progress" ||
              tournament.status === "finished") && (
              <button
                type="button"
                className="shrink-0 text-sm text-smoke hover:text-ink"
                disabled={busy}
                onClick={handleGenerateFinals}
              >
                Regénérer
              </button>
            )
          }
        >
          <BracketView
            matches={bracketMatches}
            onCreateGame={handleCreateFinalsGame}
          />
        </SectionCard>
      )}

      {/* Generate finals (only when no bracket yet) */}
      {bracketMatches.length === 0 &&
        (tournament.status === "in_progress" ||
          tournament.status === "finished") && (
          <SectionCard title="Phase finale">
            <button
              type="button"
              className="btn-primary"
              disabled={busy}
              onClick={handleGenerateFinals}
            >
              Générer la phase finale
            </button>
            <p className="mt-2 text-xs text-smoke">
              Génère le bracket à partir du classement actuel (top{" "}
              {tournament.standingRows.length} équipes).
            </p>
          </SectionCard>
        )}

      {/* Standings with dissolve/reactivate buttons */}
      {tournament.standingRows.length > 0 && (
        <SectionCard title="Équipes">
          <div className="space-y-2">
            {tournament.standingRows.map((row) => {
              const isDissolved = row.status === "dissolved";
              return (
                <div
                  key={row.teamId}
                  className={`card flex items-center justify-between gap-3 ${isDissolved ? "opacity-50" : ""}`}
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-ink">{row.teamName}</p>
                      {isDissolved && (
                        <span className="status-pill bg-gray-100 text-gray-600">
                          Dissous
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-smoke">
                      {row.points} pts, {row.countedHands}/{row.quota} levées
                      {row.blankSupportHands > 0 &&
                        `, ${row.blankSupportHands} blanches`}
                    </p>
                  </div>
                  {isDissolved ? (
                    <button
                      type="button"
                      className="btn-secondary text-xs"
                      disabled={busy || isTerminal}
                      onClick={() => handleReactivateTeam(row.teamId)}
                    >
                      Réactiver
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn-secondary text-xs text-red-600"
                      disabled={busy || isTerminal}
                      onClick={() => handleDissolveTeam(row.teamId)}
                    >
                      Dissoudre
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </SectionCard>
      )}

      {/* Pending games (Arbitrer) */}
      <SectionCard title={`Arbitrer (${pendingGames.length})`}>
        {pendingGames.length === 0 ? (
          <p className="text-sm text-smoke">Aucune partie en attente.</p>
        ) : (
          <div className="space-y-3">
            {pendingGames.map((game) => (
              <div
                key={game.id}
                className="card flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0">
                  <p className="font-semibold text-ink">{game.title}</p>
                  <p className="mt-0.5 text-sm text-smoke">
                    {game.scoreLeft} - {game.scoreRight}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <span
                    className={`status-pill ${
                      game.status === "contested"
                        ? "bg-rose-100 text-rose-700"
                        : "bg-amber-100 text-amber-700"
                    }`}
                  >
                    {game.status === "contested" ? "Contestée" : "Soumise"}
                  </span>
                  {game.type === "blank" && (
                    <span className="status-pill bg-sky-100 text-sky-700">
                      Blanc
                    </span>
                  )}
                  <button
                    type="button"
                    className="btn-primary text-xs"
                    disabled={busy}
                    onClick={() => handleForceValidate(game.id)}
                  >
                    Valider
                  </button>
                  <button
                    type="button"
                    className="btn-secondary text-xs text-red-600"
                    disabled={busy}
                    onClick={() => handleCancelGame(game.id)}
                  >
                    Annuler
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>
    </div>
  );
}
